import { Keys, ThemeMode } from '@eih-netbroker/common';
import { createTheme } from '@mui/material';
import styles from '../../context/NbThemeContext.module.scss';
import { Config } from '../config/config';

const cssPxToNumber = (input: string) => Number(input.replace('px', ''));

const DefaultBaseFontSize = Config.NetBrokerWeb.baseFontSize || cssPxToNumber(styles.fontSizeMedium);

// set up default font size on html element by inserting a style element
const element = document.createElement('style');
element.textContent = `html { font-size: ${DefaultBaseFontSize}px}`;
document.querySelector('body')!.append(element);

const fontSizes = {
  tiny: cssPxToNumber(styles.fontSizeTiny),
  small: cssPxToNumber(styles.fontSizeSmall),
  medium: cssPxToNumber(styles.fontSizeMedium),
  normal: cssPxToNumber(styles.fontSizeNormal),
};

const resolutions = {
  fulldHD: cssPxToNumber(styles.resolutionFullHd),
  highRes: cssPxToNumber(styles.resolutionHighRes),
  midRes: cssPxToNumber(styles.resolutionMidRes),
  desktop: cssPxToNumber(styles.resolutionDesktop),
};

/**
 * Returns base font size based on screen width
 *
 * @param width width of the screen
 */
const getBaseFontSize = (width: number) => {
  if (width >= resolutions.fulldHD) {
    return fontSizes.normal;
  } else if (width >= resolutions.highRes) {
    return fontSizes.normal;
  } else if (width >= resolutions.midRes) {
    return fontSizes.medium;
  } else if (width >= resolutions.desktop) {
    return fontSizes.small;
  } else {
    return fontSizes.tiny;
  }
};

const safeColor = (input: string | undefined, fallback: string) =>
  !input || !input.startsWith('#') || !input.startsWith('rgb') || !input.startsWith('rgb') ? fallback : input;

// use https://bareynol.github.io/mui-theme-creator/#Buttons
const lightTheme = createTheme({
  palette: {
    text: {
      primary: safeColor(styles.colorTextPrimary, '#303030'),
    },
    primary: {
      main: safeColor(styles.colorPrimary, '#2870ED'),
    },
    secondary: {
      main: safeColor(styles.colorSecondary, '#ffffff'),
    },
    error: {
      main: safeColor(styles.colorError, '#ef0039'),
    },
    warning: {
      main: safeColor(styles.colorWarning, '#ff6130'),
    },
    info: {
      main: safeColor(styles.colorInfo, '#a3b5c9'),
    },
    success: {
      main: safeColor(styles.colorSuccess, '#0cb43f'),
    },
    emphasize: {
      main: '#ffffff',
      contrastText: safeColor(styles.colorPrimary, '#2870ed'),
      dark: '#ffffff',
    },
  },
  typography: {
    fontSize: DefaultBaseFontSize,
    htmlFontSize: DefaultBaseFontSize,
    fontFamily: 'Inter',
  },
});

const darkTheme = createTheme({
  palette: {
    text: {
      primary: safeColor(styles.colorTextPrimaryDark, '#ffffff'),
    },
    primary: {
      main: safeColor(styles.colorPrimaryDark, '#2870ed'),
    },
    secondary: {
      main: safeColor(styles.colorSecondaryDark, '#ffffff'),
    },
    error: {
      main: safeColor(styles.colorErrorDark, '#ef0039'),
    },
    warning: {
      main: safeColor(styles.colorWarningDark, '#ff6130'),
    },
    info: {
      main: safeColor(styles.colorInfoDark, '#a3b5c9'),
    },
    success: {
      main: safeColor(styles.colorSuccessDark, '#0cb43f'),
    },
    emphasize: {
      main: '#ffffff',
      contrastText: safeColor(styles.colorPrimaryDark, '#2870ed'),
      dark: '#ffffff',
    },
  },
  typography: {
    fontSize: DefaultBaseFontSize,
    htmlFontSize: DefaultBaseFontSize,
  },
});

const initialThemeMode = (() => {
  const storedKey = window.localStorage.getItem(Keys.Theme.key);
  return !storedKey || !Object.values(ThemeMode).includes(storedKey as ThemeMode)
    ? ThemeMode.auto
    : (storedKey as ThemeMode);
})();

const isOSPrefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

const isThemeDark = (pThemeMode: ThemeMode) => {
  return pThemeMode === ThemeMode.dark || (pThemeMode === ThemeMode.auto && isOSPrefersDarkMode);
};

export const themeUtils = {
  lightTheme,
  darkTheme,
  getBaseFontSize,
  DefaultBaseFontSize,
  initialThemeMode,
  isThemeDark,
};

/**
 * Utility to express list of classes to be applied conditionally
 *
 * @example
 * cssWhen(classes.menuItem, [isExpanded, classes.menuItemIsExpanded], [isIOS, classes.menuItemIsIOS, classes.menuItemIsAndroid])
 */
export const cssWhen = (
  ...args: Array<string | undefined | null | [boolean | undefined | null | string, string, string?]>
) => {
  return args
    .map(item => {
      if (typeof item === 'string') {
        return item;
      } else if (Array.isArray(item)) {
        return !!item[0] ? item[1] : item[2] || '';
      } else if (item === undefined || item === null) {
        return '';
      } else {
        console.warn(`Invalid cssWhen argument ${args}`);
        return '';
      }
    })
    .join(' ');
};
